import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../components/Button";
import Checkbox from "../../../components/v2/Checkbox";
import Input, { MaterialInputEvent } from "../../../components/v2/Input";
import Layout from "../../../components/v2/Layout";
import ProtestResult from "../../../components/v2/ConsultarProtesto/ProtestResult";
import ProtestoService, {
  ResponseProtest,
} from "../../../services/ProtestoService";
import { isServerError } from "../../../config/api";
import { useQuery } from "../../../helpers/Utils";

import style from "./styles.module.scss";

const keyI18n = "translations.protestedTitles";

type Status = "idle" | "loading" | "success" | "error";

function downloadBase64(base64: string, filename: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function ConsultarProtesto() {
  const { t } = useTranslation();
  const query = useQuery();
  const cdaFromUrl = query.get("d");
  const passwordFromUrl = query.get("n");

  const [bulkMode, setBulkMode] = useState(false);
  const [cdaNumber, setCdaNumber] = useState(cdaFromUrl ?? "");
  const [password, setPassword] = useState(passwordFromUrl ?? "");
  const [protocols, setProtocols] = useState("");
  const [email, setEmail] = useState("");

  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");
  const [protest, setProtest] = useState<ResponseProtest>();
  const [downloading, setDownloading] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const autoSubmitted = useRef(false);

  const search = useCallback(
    async (cda: string, secret: string) => {
      setStatus("loading");
      setMessage("");
      setSuccess("");
      setProtest(undefined);

      try {
        const response = await ProtestoService.protestos(cda, secret);
        setProtest(response);
        setStatus("success");
      } catch (error) {
        if (isServerError(error)) {
          setErrors({ cdaNumber: t(`${keyI18n}.errors.invalidCredentials`) });
          setStatus("idle");
        } else {
          setMessage(t(`${keyI18n}.errors.unavailable`));
          setStatus("error");
        }
      }
    },
    [t],
  );

  useEffect(() => {
    if (cdaFromUrl && passwordFromUrl && !autoSubmitted.current) {
      autoSubmitted.current = true;
      search(cdaFromUrl, passwordFromUrl);
    }
  }, [cdaFromUrl, passwordFromUrl, search]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();

    const found: { [key: string]: string } = {};

    if (!cdaNumber.trim()) found.cdaNumber = t(`${keyI18n}.errors.required`);
    if (!password.trim()) found.password = t(`${keyI18n}.errors.required`);

    setErrors(found);

    if (Object.keys(found).length > 0) {
      return;
    }

    search(cdaNumber.trim(), password.trim());
  };

  const handleBulk = async (event: React.FormEvent) => {
    event.preventDefault();

    const list = protocols
      .split(/[\s,;]+/)
      .map((item) => item.trim())
      .filter(Boolean);
    const found: { [key: string]: string } = {};

    if (list.length === 0) found.protocols = t(`${keyI18n}.errors.required`);
    if (!email.trim()) found.email = t(`${keyI18n}.errors.required`);
    if (!password.trim()) found.password = t(`${keyI18n}.errors.required`);

    setErrors(found);

    if (Object.keys(found).length > 0) {
      return;
    }

    setStatus("loading");
    setMessage("");
    setSuccess("");

    try {
      const response = await ProtestoService.gerar_multiplos(email.trim(), password.trim(), list);
      const total = (response as { total?: number }).total ?? list.length;
      setSuccess(t(`${keyI18n}.bulk.success`, { total }));
      setStatus("idle");
    } catch (error) {
      if (isServerError(error)) {
        setMessage((error as { error: string }).error);
      } else {
        setMessage(t(`${keyI18n}.errors.unavailable`));
      }
      setStatus("error");
    }
  };

  const handleDownload = async (documentType: "cda" | "dam") => {
    if (!protest) {
      return;
    }

    setDownloading(documentType);

    try {
      const response = await ProtestoService.documentos(protest.id, documentType);
      const base64 = (response as { document?: string }).document ?? "";

      if (!base64) {
        setMessage(t(`${keyI18n}.errors.unavailable`));
        setStatus("error");
        return;
      }

      downloadBase64(
        base64,
        documentType === "cda" ? "certidao.pdf" : "boleto.pdf",
      );
    } catch (error) {
      setMessage(
        isServerError(error)
          ? (error as { error: string }).error
          : t(`${keyI18n}.errors.unavailable`),
      );
      setStatus("error");
    } finally {
      setDownloading("");
    }
  };

  const submitOnEnter = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      (event.currentTarget as HTMLElement).closest("form")?.requestSubmit();
    }
  };

  const passwordToggle = (
    <span
      aria-label={t(`${keyI18n}.fields.togglePassword`)}
      className={`material-symbols-sharp ${style.passwordToggle}`}
      onClick={() => setShowPassword((value) => !value)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setShowPassword((value) => !value);
        }
      }}
      role="button"
      tabIndex={0}
    >
      {showPassword ? "visibility_off" : "visibility"}
    </span>
  );

  const toggleBulk = (checked: boolean) => {
    setBulkMode(checked);
    setErrors({});
    setMessage("");
    setSuccess("");
    setProtest(undefined);
    setStatus("idle");
  };

  return (
    <Layout title={t(`${keyI18n}.title`)}>
      <div className={style.body}>
        <header className={style.header}>
          <h1 className={`pgm-headline-small ${style.title}`}>
            {t(`${keyI18n}.title`)}
          </h1>

          <p className={`pgm-body-large ${style.notice}`}>
            <span aria-hidden="true" className={`material-symbols-sharp ${style.noticeIcon}`}>
              warning
            </span>
            {t(`${keyI18n}.notice`)}
          </p>

          <Checkbox
            checked={bulkMode}
            id="protest-bulk"
            label={t(`${keyI18n}.bulk.toggle`)}
            labelPosition="start"
            onChange={toggleBulk}
          />
        </header>

        {bulkMode ? (
          <form className={style.grid} noValidate onSubmit={handleBulk}>
            <Input
              className={style.bulkProtocols}
              error={Boolean(errors.protocols)}
              errorText={errors.protocols}
              fullWidth
              label={t(`${keyI18n}.fields.protocols`)}
              name="protocols"
              onInput={(event: MaterialInputEvent) => setProtocols(event.target.value)}
              rows={3}
              type="textarea"
              value={protocols}
              variant="filled"
            />
            <Input
              className={style.field}
              error={Boolean(errors.email)}
              errorText={errors.email}
              fullWidth
              label={t(`${keyI18n}.fields.email`)}
              name="email"
              onInput={(event: MaterialInputEvent) => setEmail(event.target.value)}
              onKeyDown={submitOnEnter}
              type="email"
              value={email}
              variant="filled"
            />
            <Input
              className={style.field}
              error={Boolean(errors.password)}
              errorText={errors.password}
              fullWidth
              label={t(`${keyI18n}.fields.password`)}
              name="password"
              hideClearButton
              onInput={(event: MaterialInputEvent) => setPassword(event.target.value)}
              onKeyDown={submitOnEnter}
              trailingIcon={passwordToggle}
              type={showPassword ? "text" : "password"}
              value={password}
              variant="filled"
            />
            <Button
              className={style.submit}
              disabled={status === "loading"}
              icon={<span className="material-symbols-sharp">barcode</span>}
              size="small"
              type="submit"
              variant="filled"
            >
              {t(`${keyI18n}.bulk.submit`)}
            </Button>
          </form>
        ) : (
          <form className={style.grid} noValidate onSubmit={handleSearch}>
            <Input
              className={style.field}
              error={Boolean(errors.cdaNumber)}
              errorText={errors.cdaNumber}
              fullWidth
              label={t(`${keyI18n}.fields.cda`)}
              name="cdaNumber"
              onInput={(event: MaterialInputEvent) => setCdaNumber(event.target.value)}
              onKeyDown={submitOnEnter}
              value={cdaNumber}
              variant="filled"
            />
            <Input
              className={style.field}
              error={Boolean(errors.password)}
              errorText={errors.password}
              fullWidth
              label={t(`${keyI18n}.fields.password`)}
              name="password"
              hideClearButton
              onInput={(event: MaterialInputEvent) => setPassword(event.target.value)}
              onKeyDown={submitOnEnter}
              trailingIcon={passwordToggle}
              type={showPassword ? "text" : "password"}
              value={password}
              variant="filled"
            />
            <Button
              className={style.submit}
              disabled={status === "loading"}
              icon={<span className="material-symbols-sharp">search</span>}
              size="small"
              type="submit"
              variant="tonal"
            >
              {t(`${keyI18n}.submit`)}
            </Button>
          </form>
        )}

        {success && (
          <div className={style.successMessage} role="status">
            <span aria-hidden="true" className={`material-symbols-sharp ${style.successIcon}`}>
              check_circle
            </span>
            <p className={`pgm-title-large ${style.successText}`}>{success}</p>
          </div>
        )}

        {status === "error" && message && (
          <div className={style.errorMessage} role="alert">
            <span aria-hidden="true" className={`material-symbols-sharp ${style.errorIcon}`}>
              error
            </span>
            <p className={`pgm-title-large ${style.errorText}`}>{message}</p>
          </div>
        )}

        {status === "success" && protest && (
          <>
            <ProtestResult protest={protest} />

            <div className={style.actions}>
              <Button
                disabled={downloading !== "" || !protest.can_generate_billet}
                icon={<span className="material-symbols-sharp">barcode</span>}
                onClick={() => handleDownload("dam")}
                size="small"
                variant="filled"
              >
                {t(`${keyI18n}.actions.${protest.type === "dam" ? "dam" : "billet"}`)}
              </Button>

              <Button
                disabled={downloading !== ""}
                icon={<span className="material-symbols-sharp">ballot</span>}
                onClick={() => handleDownload("cda")}
                size="small"
                variant="outlined"
              >
                {t(`${keyI18n}.actions.cda`)}
              </Button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
